import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, Trash2, Search, X } from 'lucide-react';
import { initialOrders, statusColors } from '../../../Data/Dummydata';

const tabs = ['pending', 'accepted', 'denied'];

export default function LatestOrders() {
  const [orders, setOrders] = useState(initialOrders);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');

  const suggestions =
    searchQuery.length >= 3
      ? initialOrders.filter(
          (item) =>
            item.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.id.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : [];

  const handleAction = (id, action) => {
    setOrders((prev) =>
      prev.map((order) => (order.id === id ? { ...order, status: action } : order)),
    );
  };

  const clearDeniedOrders = () => {
    const remaining = orders.filter((o) => o.status !== 'denied');
    setOrders(remaining);
  };

  const filteredOrders = orders
    .filter((order) => order.status === activeTab)
    .filter(
      (order) =>
        order.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.id.toLowerCase().includes(searchQuery.toLowerCase()),
    );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h2 className="text-lg lg:text-2xl font-bold text-secondary mb-6 text-center md:text-start">
        Latest Orders
      </h2>

      {/* Tabs */}
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full text-sm lg:font-semibold capitalize transition-all duration-200 ${
                activeTab === tab
                  ? 'primary-background white-text shadow'
                  : 'bg-gray-100 text-secondary hover:bg-gray-200'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Clear Denied Button */}
        {activeTab === 'denied' && (
          <button
            onClick={clearDeniedOrders}
            className="flex items-center gap-2 text-sm text-red-600 bg-red-100 hover:bg-red-200 px-4 py-2 rounded-full"
          >
            <Trash2 size={16} /> Clear All
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by Customer and Id..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
        />
        {searchQuery && (
          <X
            className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 cursor-pointer hover:text-secondary"
            onClick={() => setSearchQuery('')}
          />
        )}
        {searchQuery.length >= 3 && (
          <ul className="absolute z-10 mt-1 w-full rounded-md bg-white border border-gray-200 shadow-sm max-h-48 overflow-auto">
            {suggestions.length > 0 ? (
              suggestions.map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => setSearchQuery(item.customer)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {item.customer} - {item.id}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-sm cursor-default">No results found</li>
            )}
          </ul>
        )}
      </div>

      {/* Orders */}
      {filteredOrders.length === 0 ? (
        <div className="text-gray-400 italic text-center">No {activeTab} orders.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 transition-all duration-300 mt-4">
          {filteredOrders.map(({ id, customer, items, amount, time, status }) => (
            <div
              key={id}
              className="bg-white border rounded-2xl p-5 shadow hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="lg:text-lg font-semibold tertiary-text-color">{customer}</h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}
                  >
                    {status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Order ID:</strong> {id}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Items:</strong> {items}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Total:</strong> ₹{amount}
                </p>
                <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Clock size={14} /> {time}
                </div>
              </div>

              {status === 'pending' ? (
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => handleAction(id, 'accepted')}
                    className="flex-1 bg-green-600 hover:bg-green-700 white-text py-2 text-sm rounded-lg flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={16} /> Accept
                  </button>
                  <button
                    onClick={() => handleAction(id, 'denied')}
                    className="flex-1 bg-red-600 hover:bg-red-700 white-text py-2 text-sm rounded-lg flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} /> Deny
                  </button>
                </div>
              ) : (
                <div className="text-center text-sm text-gray-500 italic mt-4">
                  Order was <span className="font-medium">{status}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
